import { deepCopy } from '../methods/deepCopy.js';
import { randomString } from '../methods/randomString.js';
import { getDefaultTableRowData } from './getDefaultTableRowData.js';
import { isPlainObject } from '../methods/isPlainObject.js';

const removeProps = [
    'addClass',
    'asString',
    'attributes',
    'columns',
    'createButtonLabel',
    'dataLabelName',
    'description',
    'emptyAs',
    'fields',
    'hasLoopType',
    'hasTextField',
    'help',
    'includeDraft',
    'innerBlocks',
    'innerGroups',
    'label',
    'maxCount',
    'modalCreate',
    'modalEdit',
    'options',
    'paginate',
    'params',
    'preset',
    'radio',
    'required',
    'rows',
    'selectButtonLabel',
    'status',
    'textFieldLabel',
    'type',
    'uploadDirectory'
];

/**
 * fieldBlock のバリデーション
 * @param {object} fieldBlocks
 * @param {string} handle
 */
function validateFieldBlock(fieldBlocks, handle) {
    // fieldBlocks[handle] の存在確認
    if (typeof fieldBlocks[handle] === 'undefined') {
        throw new Error(`fieldBlocks['${handle}'] is not found.`);
    }

    const fieldBlock = fieldBlocks[handle];

    // type プロパティの存在確認
    if (typeof fieldBlock.type === 'undefined') {
        throw new Error(`The 'type' property for fieldBlocks['${handle}'] is required.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`);
    }

    // table の場合のバリデーション
    if (fieldBlock.type === 'table') {
        if (!Array.isArray(fieldBlock.fields)) {
            throw new Error(
                `The 'fields' property for fieldBlocks['${handle}'] must be an array.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        } else if (!Array.isArray(fieldBlock.data)) {
            throw new Error(
                `The 'data' property for fieldBlocks['${handle}'] must be an array.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        }
    }
    // データの初期値をセット: 'table-vertical-fixed', 'chunk'
    else if (['table-vertical', 'table-vertical-fixed', 'chunk'].includes(fieldBlock.type)) {
        if (!Array.isArray(fieldBlock.fields)) {
            throw new Error(
                `The 'fields' property for fieldBlocks['${handle}'] must be an array.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        } else if (!isPlainObject(fieldBlock.data)) {
            throw new Error(
                `The 'data' property for fieldBlocks['${handle}'] must be an object.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        }
    }
    // データの初期値をセット: 'multi-column'
    else if (fieldBlock.type === 'multi-column') {
        if (!isPlainObject(fieldBlock.data)) {
            throw new Error(
                `The 'data' property for fieldBlocks['${handle}'] must be an object.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        }
        if (!Array.isArray(fieldBlock.columns)) {
            throw new Error(
                `The 'columns' property for fieldBlocks['${handle}'] must be an array.\n\n${JSON.stringify(fieldBlock, null, 2)}\n`
            );
        }
        fieldBlock.columns.forEach((column, index) => {
            if (!Array.isArray(column.fields)) {
                throw new Error(
                    `The 'fields' property for fieldBlocks['${handle}'].columns[${index}] must be an array.\n\n${JSON.stringify(
                        fieldBlock,
                        null,
                        2
                    )}\n`
                );
            }
        });
    }
    return fieldBlock;
}

/**
 * 不要なプロパティを再起的に削除
 * @param obj
 * @param removeProps
 */
function removeProperties(obj, removeProps) {
    // オブジェクトの各プロパティをチェック
    for (const prop in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, prop)) {
            // 削除対象のプロパティが見つかったら削除
            if (removeProps.includes(prop)) {
                delete obj[prop];
            } else if (typeof obj[prop] === 'object' && obj[prop] !== null) {
                // プロパティがオブジェクトまたは配列の場合、再帰的に処理を行う
                removeProperties(obj[prop], removeProps);
            }
        }
    }
}

/**
 * Create a new item
 * @param {object} fieldBlocks
 * @param {string} handle
 */
export function createNewItem(fieldBlocks, handle, additionalRemoveProps = []) {
    const fieldBlock = validateFieldBlock(fieldBlocks, handle);
    const newItem = deepCopy(fieldBlock);

    // 不要なプロパティを削除
    removeProperties(newItem, removeProps);

    // 必須プロパティをセット
    newItem.handle = handle;
    newItem.id = randomString();
    newItem.expanded = newItem.expanded ?? true;

    // データの初期値をセット: 'table'
    if (fieldBlock.type === 'table' && fieldBlock.data.length === 0) {
        newItem.data.push(getDefaultTableRowData(fieldBlocks, handle));
    }
    // データの初期値をセット: 'table-vertical-fixed', 'chunk'
    else if (['table-vertical', 'table-vertical-fixed', 'chunk'].includes(fieldBlock.type) && Object.keys(fieldBlock.data).length === 0) {
        newItem.data = { ...getDefaultTableRowData(fieldBlocks, handle) };
    }
    // データの初期値をセット: 'multipurpose'
    else if (fieldBlock.type === 'multipurpose') {
        if (isPlainObject(fieldBlock.data)) {
            newItem.data = deepCopy(fieldBlock.data);
        } else {
            newItem.data = {};
        }
    }
    // データの初期値をセット: 'multi-column'
    else if (fieldBlock.type === 'multi-column') {
        fieldBlock.columns.forEach((column) => {
            if (typeof column.id === 'undefined') {
                column.id = randomString();
            }
            column.fields.forEach((handleInColumnFields) => {
                const innerFieldBlock = validateFieldBlock(fieldBlocks, handleInColumnFields);
                // データの初期値をセット: 'multi-column' > 'table'
                if (innerFieldBlock.type === 'table' && innerFieldBlock.data.length === 0) {
                    newItem.data[handleInColumnFields] = [getDefaultTableRowData(fieldBlocks, handleInColumnFields)];
                }
                // データの初期値をセット: 'multi-column' > 'table-vertical-fixed'
                else if (
                    ['table-vertical', 'table-vertical-fixed'].includes(innerFieldBlock.type) &&
                    Object.keys(innerFieldBlock.data).length === 0
                ) {
                    newItem.data[handleInColumnFields] = { ...getDefaultTableRowData(fieldBlocks, handleInColumnFields) };
                }
                // データの初期値をセット: 'multi-column' > 'contentData'
                else if (innerFieldBlock.type === 'contentData') {
                    newItem.data[handleInColumnFields] = deepCopy(innerFieldBlock.data);
                }
                // データの初期値をセット: 'multi-column' > その他
                else {
                    newItem.data[handleInColumnFields] = deepCopy(innerFieldBlock.data);
                }
            });
        });
    }
    additionalRemoveProps.forEach((prop) => {
        delete newItem[prop];
    });
    return newItem;
}
